import React, { useState, useEffect } from "react";
import {
  BarChart3,
  Users,
  Award,
  BookOpen,
  Activity,
  Download,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  Target,
  Loader,
  TrendingUp,
  Calendar,
  CheckCircle,
  Percent,
  Star,
  Trophy,
} from "lucide-react";
import { apiService } from "../../services/api";
import styles from "./AdminAnalytics.module.css";

const AdminAnalytics = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState("last_30_days");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({
    overview: {
      totalUsers: 0,
      activeUsers: 0,
      totalAssessments: 0,
      completedAssessments: 0,
      averageScore: 0,
      completionRate: 0,
    },
    userGrowth: [],
    assessmentStats: [],
    skillPerformance: [],
    userActivity: [],
  });

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [
          overviewData,
          userGrowthData,
          assessmentData,
          skillData,
          activityData,
        ] = await Promise.allSettled([
          apiService.admin.getAnalyticsOverview(selectedTimeframe),
          apiService.admin.getUserGrowthData(selectedTimeframe),
          apiService.admin.getAssessmentAnalytics(selectedTimeframe),
          apiService.admin.getSkillPerformanceData(selectedTimeframe),
          apiService.admin.getUserActivityData(selectedTimeframe),
        ]);

        if (
          overviewData.status === "fulfilled" &&
          overviewData.value?.success
        ) {
          setAnalyticsData((prev) => ({
            ...prev,
            overview: overviewData.value.data,
          }));
        }

        if (
          userGrowthData.status === "fulfilled" &&
          userGrowthData.value?.success
        ) {
          setAnalyticsData((prev) => ({
            ...prev,
            userGrowth: userGrowthData.value.data,
          }));
        }

        if (
          assessmentData.status === "fulfilled" &&
          assessmentData.value?.success
        ) {
          setAnalyticsData((prev) => ({
            ...prev,
            assessmentStats: assessmentData.value.data,
          }));
        }

        if (skillData.status === "fulfilled" && skillData.value?.success) {
          setAnalyticsData((prev) => ({
            ...prev,
            skillPerformance: skillData.value.data,
          }));
        }

        if (
          activityData.status === "fulfilled" &&
          activityData.value?.success
        ) {
          setAnalyticsData((prev) => ({
            ...prev,
            userActivity: activityData.value.data,
          }));
        }
      } catch (error) {
        console.error("Failed to fetch analytics data:", error);
        setError("Failed to load analytics data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [selectedTimeframe]);

  const handleExportReport = async () => {
    try {
      const blob = await apiService.admin.exportAnalyticsReport(
        selectedTimeframe,
        "csv",
      );
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `analytics-${selectedTimeframe}-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error("Failed to export report:", error);
      alert("Failed to export report. Please try again.");
    }
  };

  const handleRefreshData = () => {
    setLoading(true);
    setSelectedTimeframe((prev) => prev);
  };

  if (loading) {
    return (
      <div className={`${styles.adminAnalytics} ${styles.loading}`}>
        <div className={styles.loadingContainer}>
          <Loader size={48} className={styles.spinner} />
          <h2>Loading Analytics...</h2>
          <p>Gathering insights from your platform data</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${styles.adminAnalytics} ${styles.error}`}>
        <div className={styles.errorContainer}>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={handleRefreshData}>
            <RefreshCw size={18} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const engagementRate =
    analyticsData.overview.totalUsers > 0
      ? (
          (analyticsData.overview.activeUsers /
            analyticsData.overview.totalUsers) *
          100
        ).toFixed(1)
      : 0;
  const topSkill =
    analyticsData.skillPerformance.length > 0
      ? analyticsData.skillPerformance[0]
      : null;

  return (
    <div className={styles.adminAnalytics}>
      {/* Header */}
      <div className={styles.analyticsHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitleSection}>
            <div className={styles.titleIconWrapper}>
              <BarChart3 size={28} />
            </div>
            <div>
              <h1>Analytics Dashboard</h1>
              <p>Real-time insights and performance metrics</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            <div className={styles.timeframeWrapper}>
              <Calendar size={18} />
              <select
                value={selectedTimeframe}
                onChange={(e) => setSelectedTimeframe(e.target.value)}
                className={styles.timeframeSelect}
              >
                <option value="last_7_days">Last 7 Days</option>
                <option value="last_30_days">Last 30 Days</option>
                <option value="last_90_days">Last 90 Days</option>
                <option value="last_year">Last Year</option>
              </select>
            </div>
            <button className="btn btn-secondary" onClick={handleRefreshData}>
              <RefreshCw size={18} />
              Refresh
            </button>
            <button className="btn btn-primary" onClick={handleExportReport}>
              <Download size={18} />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className={styles.dashboardContent}>
        {/* Stats Cards */}
        <div className={styles.statsGrid}>
          <div className={`${styles.statCard} ${styles.blue}`}>
            <div className={styles.statIcon}>
              <Users size={24} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>
                {analyticsData.overview.totalUsers.toLocaleString()}
              </div>
              <div className={styles.statLabel}>Total Users</div>
              <div className={styles.statMeta}>{engagementRate}% Active</div>
            </div>
          </div>

          <div className={`${styles.statCard} ${styles.green}`}>
            <div className={styles.statIcon}>
              <Trophy size={24} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>
                {analyticsData.overview.completedAssessments.toLocaleString()}
              </div>
              <div className={styles.statLabel}>Completions</div>
              <div className={styles.statMeta}>
                {analyticsData.overview.completionRate}% Rate
              </div>
            </div>
          </div>

          <div className={`${styles.statCard} ${styles.orange}`}>
            <div className={styles.statIcon}>
              <Star size={24} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>
                {analyticsData.overview.averageScore}%
              </div>
              <div className={styles.statLabel}>Average Score</div>
              <div className={styles.statMeta}>
                {topSkill && `Top: ${topSkill.skill}`}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className={`${styles.statsGrid} ${styles.quickStats}`}>
          <div className={styles.statCard}>
            <div className={`${styles.statIcon} ${styles.blue}`}>
              <Activity size={20} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>
                {analyticsData.overview.activeUsers.toLocaleString()}
              </div>
              <div className={styles.statLabel}>Active Users</div>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={`${styles.statIcon} ${styles.purple}`}>
              <BookOpen size={20} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>
                {analyticsData.overview.totalAssessments}
              </div>
              <div className={styles.statLabel}>Assessments</div>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={`${styles.statIcon} ${styles.orangeBg}`}>
              <Percent size={20} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>
                {analyticsData.overview.completionRate}%
              </div>
              <div className={styles.statLabel}>Completion Rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.analyticsContent}>
        {/* User Growth Chart */}
        {analyticsData.userGrowth.length > 0 && (
          <div className={`${styles.analyticsSection} ${styles.fullWidth}`}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                <TrendingUp size={22} />
                <h2>User Growth Trends</h2>
              </div>
              <div className={styles.sectionLegend}>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendDot} ${styles.blue}`}></span>
                  Total
                </span>
                <span className={styles.legendItem}>
                  <span
                    className={`${styles.legendDot} ${styles.green}`}
                  ></span>
                  Active
                </span>
                <span className={styles.legendItem}>
                  <span
                    className={`${styles.legendDot} ${styles.orange}`}
                  ></span>
                  New
                </span>
              </div>
            </div>
            <div className={styles.sectionBody}>
              <div className={styles.chartWrapper}>
                <div className={styles.chartContainer}>
                  {analyticsData.userGrowth.map((data, index) => {
                    const maxValue = Math.max(
                      ...analyticsData.userGrowth.map((d) => d.totalUsers),
                    );
                    const totalHeight = (data.totalUsers / maxValue) * 100;
                    const activeHeight = (data.activeUsers / maxValue) * 100;
                    const newHeight = (data.newUsers / maxValue) * 100;

                    return (
                      <div key={index} className={styles.chartBarGroup}>
                        <div className={styles.chartBars}>
                          <div
                            className={`${styles.chartBar} ${styles.total}`}
                            style={{ height: `${totalHeight}%` }}
                            title={`Total: ${data.totalUsers}`}
                          >
                            <span className={styles.barValue}>
                              {data.totalUsers}
                            </span>
                          </div>
                          <div
                            className={`${styles.chartBar} ${styles.active}`}
                            style={{ height: `${activeHeight}%` }}
                            title={`Active: ${data.activeUsers}`}
                          >
                            <span className={styles.barValue}>
                              {data.activeUsers}
                            </span>
                          </div>
                          <div
                            className={`${styles.chartBar} ${styles.new}`}
                            style={{ height: `${newHeight}%` }}
                            title={`New: ${data.newUsers}`}
                          >
                            <span className={styles.barValue}>
                              {data.newUsers}
                            </span>
                          </div>
                        </div>
                        <div className={styles.chartLabel}>
                          {new Date(data.date).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Top Assessments */}
        {analyticsData.assessmentStats.length > 0 && (
          <div className={styles.analyticsSection}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                <Award size={22} />
                <h2>Top Assessments</h2>
              </div>
              <span className={styles.sectionBadge}>
                {analyticsData.assessmentStats.length}
              </span>
            </div>
            <div className={styles.sectionBody}>
              <div className={styles.assessmentList}>
                {analyticsData.assessmentStats
                  .slice(0, 5)
                  .map((assessment, index) => (
                    <div key={index} className={styles.assessmentItem}>
                      <div className={styles.assessmentRank}>{index + 1}</div>
                      <div className={styles.assessmentDetails}>
                        <div className={styles.assessmentName}>
                          {assessment.name}
                        </div>
                        <div className={styles.assessmentMeta}>
                          <span
                            className={`${styles.difficultyTag} ${styles[assessment.difficulty.toLowerCase()]}`}
                          >
                            {assessment.difficulty}
                          </span>
                          <span className={styles.completionCount}>
                            <CheckCircle size={14} />
                            {assessment.completions} completions
                          </span>
                        </div>
                      </div>
                      <div className={styles.assessmentScore}>
                        <div className={styles.scoreCircle}>
                          <svg
                            viewBox="0 0 36 36"
                            className={styles.circularChart}
                          >
                            <path
                              className={styles.circleBg}
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                            <path
                              className={styles.circle}
                              strokeDasharray={`${assessment.avgScore}, 100`}
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                            <text
                              x="18"
                              y="20.35"
                              className={styles.percentage}
                            >
                              {Math.round(assessment.avgScore)}
                            </text>
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Skill Performance */}
        {analyticsData.skillPerformance.length > 0 && (
          <div className={styles.analyticsSection}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                <Target size={22} />
                <h2>Skill Performance</h2>
              </div>
              <span className={styles.sectionBadge}>
                {analyticsData.skillPerformance.length}
              </span>
            </div>
            <div className={styles.sectionBody}>
              <div className={styles.skillList}>
                {analyticsData.skillPerformance
                  .slice(0, 5)
                  .map((skill, index) => (
                    <div key={index} className={styles.skillItem}>
                      <div className={styles.skillHeader}>
                        <div className={styles.skillName}>{skill.skill}</div>
                        <div
                          className={`${styles.trendBadge} ${styles[skill.trend]}`}
                        >
                          {skill.trend === "up" && <ArrowUp size={14} />}
                          {skill.trend === "down" && <ArrowDown size={14} />}
                          {skill.trend === "stable" && (
                            <div className={styles.stableLine} />
                          )}
                        </div>
                      </div>
                      <div className={styles.skillMetrics}>
                        <div className={styles.skillMetric}>
                          <span className={styles.metricLabelSmall}>Score</span>
                          <span className={styles.metricValueSmall}>
                            {skill.avgScore}%
                          </span>
                        </div>
                        <div className={styles.skillMetric}>
                          <span className={styles.metricLabelSmall}>
                            Attempts
                          </span>
                          <span className={styles.metricValueSmall}>
                            {skill.completions}
                          </span>
                        </div>
                      </div>
                      <div className={styles.skillProgress}>
                        <div
                          className={styles.skillProgressFill}
                          style={{ width: `${skill.avgScore}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
